import * as amplitude from '@amplitude/unified';
import { useAuth0 } from '@auth0/auth0-react';
import {
  CookieConsent,
} from 'components';
import { AppSidebar } from 'components/app-sidebar';
import { SidebarInset, SidebarProvider } from 'components/ui/sidebar';
import { Toaster } from 'components/ui/sonner';
import { CurrentUser, useCurrentUser } from 'hooks/use-current-user';
import { ErrorBoundary } from 'layout';
import get from 'lodash/get';
import isString from 'lodash/isString';
import startsWith from 'lodash/startsWith';
import { ErrorPage, LoadingPage } from 'pages';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { security } from 'shared';

interface PageLayoutProps {
  children?: React.ReactElement;
}

const PageLayout = ({ children }: PageLayoutProps) => {
  const auth0 = useAuth0();
  const history = useHistory();
  const { location } = history;

  // OilyticsClient.create() resolves tokens through the shared security
  // module, so the Auth0 token getter must be registered before the user
  // query is allowed to run.
  const [tokenReady, setTokenReady] = useState(false);
  useEffect(() => {
    if (!auth0.isLoading && auth0.isAuthenticated) {
      security.setAccessTokenSilently(auth0.getAccessTokenSilently);
      setTokenReady(true);
    }
  }, [auth0.isLoading, auth0.isAuthenticated, auth0.getAccessTokenSilently]);

  const userQuery = useCurrentUser({
    enabled: !auth0.isLoading && auth0.isAuthenticated && tokenReady,
  });
  const user: CurrentUser = userQuery.data ?? {};
  const userError = userQuery.error
    ? get(userQuery.error, 'error') || userQuery.error
    : null;

  const login = () => {
    amplitude.track('login');
    auth0.loginWithRedirect({
      appState: {
        returnTo: `${location.pathname}${location.search}`,
      },
    });
  };

  const logout = () => {
    amplitude.track('logout');
    auth0.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  // When the users refresh token has expired, auth0 returns an error with error =  'invalid_grant' and
  // error_description = 'Unknown or invalid refresh token.' Since we only propogate the error, assume that
  // any invalid grant is because of an expired refresh token and prompt the user to login again
  const shouldRedirect = !!(!auth0.isLoading && auth0.error && auth0.error.error_description === 'Unknown or invalid refresh token.') || userError === 'invalid_grant';

  useEffect(() => {
    // Logout user and clear tokens if refresh token is invalid
    if (shouldRedirect) {
      auth0.logout({
        logoutParams: {
          localOnly: true,
        },
      });
      auth0.loginWithRedirect({
        appState: {
          returnTo: `${location.pathname}${location.search}`,
        },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRedirect]);

  let error = userError || get(auth0, 'error');
  // Convert string error to object for ErrorPage
  if (isString(error)) {
    console.warn('ErrorPage received a string error. Converting to object.', error);
    error = { name: 'Error', message: error };
  }
  const isLoading = auth0.isLoading || (!error && !user.email && auth0.isAuthenticated);

  let loadingMessage = 'Loading user';
  if (auth0.isLoading) {
    loadingMessage = 'Initializing';
  } else if (shouldRedirect) {
    loadingMessage = 'Redirecting you to the login page';
  }

  return (
    <React.Fragment>
      <SidebarProvider defaultOpen={window.innerWidth >= 1024}>
        <div className="flex h-screen w-screen">
          <AppSidebar
            showNavMain={auth0.isAuthenticated && !!user.email}
            isAuthenticated={auth0.isAuthenticated}
            user={auth0.isAuthenticated && user.email ? { name: user.name || user.email, email: user.email } : null}
            onLogin={login}
            onLogout={logout}
          />
          <SidebarInset className="flex-1 min-w-0">
            {/* Chat and geopolitics are responsive — they manage their own width
                constraints and shouldn't be forced to 1024px. Other pages still
                need the min-width to keep dashboards/watchlists laid out. */}
            <div className={`w-full h-full${location.pathname === '/chat' || startsWith(location.pathname, '/chat/') || location.pathname === '/geopolitics' ? '' : ' min-w-[1024px] p-4'}`}>
              {(isLoading && (!error || shouldRedirect)) && <LoadingPage message={loadingMessage} />}
              {!!error && !shouldRedirect && <ErrorPage error={error} />}
              {(!isLoading && !error && !shouldRedirect) && (<ErrorBoundary>{children}</ErrorBoundary>)}
            </div>
          </SidebarInset>
        </div>
        {/* duration matches the old MUI Snackbar's autoHideDuration */}
        <Toaster position="top-right" duration={5000} />
        {(!isLoading && !error && !shouldRedirect) && <CookieConsent />}
      </SidebarProvider>
    </React.Fragment>
  );
};

export default PageLayout;
